import { faFont, faSave, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type FC, useState } from "react";
import { ValidationError } from "../api/api";
import { type FlowCard, CardAction } from "../api/dto/FlowCard";
import {
  CARD_ACTION_ICON_CHECK,
  CARD_ACTION_ICON_TIMES,
  cardActionIconFor,
  cardActionIcons,
} from "../components/cardFlowEditor/cardActionIcon";
import { ModalFooter } from "../components/layout/ModalProvider";
import { useRefresh } from "../components/RefreshController";
import { Input, Select } from "@ventumsoftware/react-toolkit";

const actionTypeOptions: Record<string, string> = {
  green: "Groen (positief)",
  amber: "Oranje",
  red: "Rood (negatief)",
};

const TEXT_MODE = "text";

// The label a saved action shows is either one of the two reserved icons or free text — a tile per
// option (rather than a two-step dropdown) so the icons themselves are visible while choosing, per the
// user's ask. No faCursorI/faIBeam exists in this FontAwesome set — faFont (the general "this is text"
// glyph) is the closest fit for the free-text tile. Choosing a tile also picks its `actionType` as the
// color, which can still be changed afterwards.
const labelModeTiles: { mode: string; icon: typeof faFont; label: string; actionType: string }[] = [
  {
    mode: CARD_ACTION_ICON_CHECK,
    icon: cardActionIcons[CARD_ACTION_ICON_CHECK],
    label: "Vinkje",
    actionType: "green",
  },
  {
    mode: CARD_ACTION_ICON_TIMES,
    icon: cardActionIcons[CARD_ACTION_ICON_TIMES],
    label: "Kruisje",
    actionType: "red",
  },
  { mode: TEXT_MODE, icon: faFont, label: "Eigen tekst", actionType: "amber" },
];

// Create-or-edit for one CardAction on one source card — `existingAction` undefined means
// create. The next card isn't chosen here: it's whatever the action's arrow was dragged onto in the
// flow editor (`initialNextCardUuid`; the completion placeholder means none). Always saves through
// `updateCard` (a full-replace), reading the source card fresh via `getFreshCards` at submit time
// (not a value captured when the modal opened) so a concurrent content edit landing in between can't
// get silently clobbered.
export const EditCardActionModal: FC<{
  cards: FlowCard[];
  updateCard: (card: FlowCard) => Promise<FlowCard>;
  sourceCardUuid: string;
  existingAction?: CardAction;
  initialNextCardUuid?: string | null;
  // When provided, called with the freshly saved source card instead of firing a global refresh() —
  // lets the flow editor patch its own local state instead of triggering an app-wide resource refetch.
  onSaved?: (card: FlowCard) => void;
  // When provided, used instead of `props.cards` for save-time lookups — `props.cards` can go stale
  // if other local state changed while this modal was open (this reusable modal's `body` is a closure
  // captured once at open() time; see CardFlowEditor.tsx's cardsRef comment for why).
  getFreshCards?: () => FlowCard[];
}> = (props) => {
  const refresh = useRefresh();
  const [labelMode, setLabelMode] = useState<string>(
    props.existingAction && cardActionIconFor(props.existingAction.answerValue)
      ? props.existingAction.answerValue
      : TEXT_MODE,
  );
  // Kept separate from `labelMode` so switching tiles back and forth never loses what was typed —
  // the actually-saved `answerValue` (derived below) is only ever the custom text OR the chosen icon
  // marker, never both at once.
  const [customText, setCustomText] = useState<string>(
    props.existingAction && !cardActionIconFor(props.existingAction.answerValue)
      ? props.existingAction.answerValue
      : "",
  );
  const answerValue = labelMode === TEXT_MODE ? customText : labelMode;
  // A new action starts on the "Eigen tekst" tile (see labelMode above), so with that tile's color.
  const [actionType, setActionType] = useState<string>(props.existingAction?.actionType ?? "amber");
  const nextCardUuid = props.existingAction?.nextCardUuid ?? props.initialNextCardUuid ?? null;
  const [errors, setErrors] = useState<string[]>([]);

  const freshSourceCard = () => (props.getFreshCards?.() ?? props.cards).find((c) => c.uuid === props.sourceCardUuid);

  const save = async () => {
    const sourceCard = freshSourceCard();
    if (!sourceCard) return;

    const newAction = new CardAction(
      props.existingAction?.id ?? crypto.randomUUID(),
      answerValue,
      actionType,
      nextCardUuid,
    );
    const actions = props.existingAction
      ? sourceCard.actions.map((a) => (a.id === newAction.id ? newAction : a))
      : [...sourceCard.actions, newAction];

    const updated = await props.updateCard({ ...sourceCard, actions }).catch((e) => {
      if (e instanceof ValidationError) {
        setErrors(Object.values(e.errors).flat());
      }
      throw e;
    });
    if (props.onSaved) {
      props.onSaved(updated);
    } else {
      refresh();
    }
  };

  const remove = async () => {
    const sourceCard = freshSourceCard();
    if (!sourceCard || !props.existingAction) return;

    const actions = sourceCard.actions.filter((a) => a.id !== props.existingAction?.id);
    const updated = await props.updateCard({ ...sourceCard, actions }).catch((e) => {
      if (e instanceof ValidationError) {
        setErrors(Object.values(e.errors).flat());
      }
      throw e;
    });
    if (props.onSaved) {
      props.onSaved(updated);
    } else {
      refresh();
    }
  };

  return (
    <div className={"flex flex-col space-y-4"}>
      <div>
        <span className={"text-sm text-slate-600"}>Label</span>
        <div className={"flex space-x-2 mt-1"}>
          {labelModeTiles.map((tile) => (
            <LabelModeTile
              key={tile.mode}
              icon={tile.icon}
              label={tile.label}
              active={labelMode === tile.mode}
              onClick={() => {
                setLabelMode(tile.mode);
                setActionType(tile.actionType);
              }}
            />
          ))}
        </div>
      </div>
      {labelMode === TEXT_MODE && (
        <Input type={"text"} focus label={"Antwoordwaarde"} value={customText} onChange={setCustomText} />
      )}
      <Select label={"Kleur"} options={actionTypeOptions} value={actionType} onChange={setActionType} />
      {errors.length > 0 && (
        <div className={"text-red-600 text-sm"}>
          {errors.map((error) => (
            <div key={error}>{error}</div>
          ))}
        </div>
      )}
      <ModalFooter
        text={"Opslaan"}
        icon={faSave}
        onSubmit={save}
        disabled={!answerValue}
        textSecondary={props.existingAction ? "Verwijderen" : undefined}
        iconSecondary={faTrash}
        onSubmitSecondary={props.existingAction ? remove : undefined}
      />
    </div>
  );
};

const LabelModeTile: FC<{
  icon: typeof faFont;
  label: string;
  active: boolean;
  onClick: () => void;
}> = (props) => (
  <button
    type={"button"}
    onClick={props.onClick}
    className={`flex-1 flex flex-col items-center justify-center space-y-1 py-3 rounded border-2 ${
      props.active ? "border-brand-700 bg-brand-50 text-brand-900" : "border-slate-200 text-slate-500 hover:bg-slate-50"
    }`}
  >
    <FontAwesomeIcon icon={props.icon} className={"text-lg"} />
    <span className={"text-xs font-medium"}>{props.label}</span>
  </button>
);
